import { useState } from 'react';
import { Building, Loading2 } from 'assets/icons';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import useText from 'hooks/useText';
import { pickSuggestions, upcomingText } from 'pages/prospection/callListItems';

const NO_UPCOMING = { callbacks: 0, retries: 0 };

/**
 * The right column once nothing is left to call: the end of the day's list,
 * or an empty one. It suggests prospects from the global list; `onAdd`
 * returns a Promise, and the button waits for it.
 */
const SidePanel = ({ day, state, prospects, now, onAdd }) => {
  const { text, plural } = useText('prospection');
  const [adding, setAdding] = useState(null);
  const finished = state === 'finished';

  const textOf = ({ key, count, values }) =>
    count === undefined ? text(key, values) : plural(key, count, values);

  const suggestions = prospects ? pickSuggestions(prospects, now) : [];
  const hasProspects =
    (day?.activeProspects || 0) > 0 || (day?.made || []).length > 0;

  const add = (prospect) => {
    setAdding(prospect.company.id);
    Promise.resolve(onAdd(prospect))
      .catch(() => {})
      .finally(() => setAdding(null));
  };

  const renderSuggestions = () => {
    if (prospects === null || prospects === undefined) {
      return (
        <div className="flex justify-center py-8">
          <Loading2 wh={24} color="#9CA3AF" />
        </div>
      );
    }
    if (!suggestions.length) {
      return (
        <CompactEmptyState
          compact
          glyph={hasProspects ? 'squareCheck' : 'inbox'}
          title={text(
            hasProspects ? 'side.noSuggestTitle' : 'side.noProspectTitle',
          )}
          description={text(
            hasProspects ? 'side.noSuggestText' : 'side.noProspectText',
          )}
        />
      );
    }
    return (
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
        {suggestions.map(({ prospect, subtitle }) => (
          <li
            key={prospect.company.id}
            className="flex items-center gap-3 px-4 py-3"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-500">
              <Building wh={18} color="currentColor" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="ft-sm truncate font-medium text-gray-900">
                {prospect.company.name || '-'}
              </div>
              <div className="ft-xs truncate text-gray-500">
                {textOf(subtitle)}
              </div>
            </div>
            <Button
              color="SECONDARY"
              size="SM"
              disabled={adding === prospect.company.id}
              onClick={() => add(prospect)}
            >
              {text('side.add')}
            </Button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="ft-xl font-semibold text-gray-900">
          {text(finished ? 'side.finishedTitle' : 'side.readyTitle')}
        </h2>
        <p className="ft-sm mt-1 text-gray-500">
          {finished
            ? textOf(upcomingText(day?.upcoming || NO_UPCOMING))
            : text('side.readyText')}
        </p>
      </div>
      <div className="flex flex-col gap-3">
        <h3 className="ft-sm font-medium text-gray-700">
          {text('side.suggested')}
        </h3>
        {renderSuggestions()}
      </div>
    </div>
  );
};

export default SidePanel;
